// src/render/symbols.ts
// Per-color shape glyphs (spec §5.3): each Color maps to a distinct symbol so a child can match
// block-symbol → gate-symbol without relying on color (colorblind + pre-reader friendly).
// Pure canvas drawers — no DOM, no state. The caller sets ctx.fillStyle before calling.

import type { Color } from '../game/types';

/** The six distinct glyphs, one per palette color. */
export type SymbolShape = 'circle' | 'triangle' | 'square' | 'star' | 'heart' | 'diamond';

/** Locked Color → SymbolShape mapping (canonical COLORS order, §5.3 suggested set). */
const SHAPE_BY_COLOR: Readonly<Record<Color, SymbolShape>> = {
  red: 'circle',
  blue: 'triangle',
  green: 'square',
  yellow: 'star',
  purple: 'heart',
  orange: 'diamond',
};

export function symbolForColor(color: Color): SymbolShape {
  return SHAPE_BY_COLOR[color];
}

/**
 * Trace `shape` centered at (cx, cy), sized to fit within a square of side `size`
 * (the glyph's max extent == size). Builds a path on `ctx`; the caller fills/strokes
 * with the current style. Leaves no stroke/fill itself.
 */
export function tracePath(
  ctx: CanvasRenderingContext2D,
  shape: SymbolShape,
  cx: number,
  cy: number,
  size: number,
): void {
  const r = size / 2;
  ctx.beginPath();
  switch (shape) {
    case 'circle':
      ctx.arc(cx, cy, r * 0.92, 0, Math.PI * 2);
      break;
    case 'square': {
      // Rounded square so it never reads as the block itself.
      const s = r * 0.82;
      roundRectPath(ctx, cx - s, cy - s, s * 2, s * 2, s * 0.28);
      break;
    }
    case 'triangle': {
      const t = r * 0.96;
      // Upward equilateral-ish triangle, vertically centered by centroid.
      const top = cy - t * 0.86;
      const bottom = cy + t * 0.62;
      ctx.moveTo(cx, top);
      ctx.lineTo(cx + t * 0.92, bottom);
      ctx.lineTo(cx - t * 0.92, bottom);
      ctx.closePath();
      break;
    }
    case 'diamond': {
      const d = r * 0.98;
      ctx.moveTo(cx, cy - d);
      ctx.lineTo(cx + d * 0.82, cy);
      ctx.lineTo(cx, cy + d);
      ctx.lineTo(cx - d * 0.82, cy);
      ctx.closePath();
      break;
    }
    case 'star':
      traceStar(ctx, cx, cy, r * 0.98, r * 0.45, 5);
      break;
    case 'heart':
      traceHeart(ctx, cx, cy, r * 0.98);
      break;
    default: {
      const _exhaustive: never = shape;
      return _exhaustive;
    }
  }
}

/** Convenience: trace + fill with the current ctx.fillStyle. */
export function drawSymbol(
  ctx: CanvasRenderingContext2D,
  color: Color,
  cx: number,
  cy: number,
  size: number,
): void {
  tracePath(ctx, symbolForColor(color), cx, cy, size);
  ctx.fill();
}

// ---- primitives ------------------------------------------------------------

function traceStar(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  points: number,
): void {
  const step = Math.PI / points;
  // Start at the top point.
  let angle = -Math.PI / 2;
  ctx.moveTo(cx + Math.cos(angle) * outer, cy + Math.sin(angle) * outer);
  for (let i = 0; i < points; i++) {
    angle += step;
    ctx.lineTo(cx + Math.cos(angle) * inner, cy + Math.sin(angle) * inner);
    angle += step;
    ctx.lineTo(cx + Math.cos(angle) * outer, cy + Math.sin(angle) * outer);
  }
  ctx.closePath();
}

function traceHeart(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  // A symmetric heart built from two top lobes + a bottom point.
  const top = cy - r * 0.55;
  const bottomY = cy + r * 0.92;
  ctx.moveTo(cx, top + r * 0.28);
  ctx.bezierCurveTo(cx - r * 0.5, top - r * 0.5, cx - r * 1.1, top + r * 0.45, cx, bottomY);
  ctx.bezierCurveTo(cx + r * 1.1, top + r * 0.45, cx + r * 0.5, top - r * 0.5, cx, top + r * 0.28);
  ctx.closePath();
}

/** Reusable rounded-rect path (clamped radius). Exported for canvas.ts reuse. */
export function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
): void {
  const r = Math.max(0, Math.min(radius, Math.min(w, h) / 2));
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}
